import { FormEvent, useState } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { useLoginMutation, useResendVerificationMutation } from "@/store/api";
import { useAppDispatch, useAppSelector } from "@/store";
import { setSession } from "@/store/authSlice";
import { toast } from "sonner";

export default function Login() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAppSelector((s) => s.auth.token);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [login, { isLoading }] = useLoginMutation();
  const [resend, { isLoading: resending }] = useResendVerificationMutation();

  if (token) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNeedsVerification(false);
    try {
      const res = await login({ email, password }).unwrap();
      dispatch(setSession({ token: res.token, user: res.user }));
      toast.success(`Welcome back, ${res.user.email}`);
      const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";
      navigate(from, { replace: true });
    } catch (e) {
      const detail = (e as { data?: { detail?: string }; status?: number })?.data?.detail;
      if (detail === "email_not_verified") {
        setNeedsVerification(true);
        setError("Please verify your email before signing in.");
      } else {
        setError(detail ?? "Sign-in failed");
      }
    }
  };

  const onResend = async () => {
    if (!email) return;
    try {
      await resend({ email }).unwrap();
      toast.success("Verification email re-sent. Check your inbox (and spam).");
    } catch {
      toast.error("Couldn't re-send right now. Try again in a minute.");
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-bg text-fg p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>
            New here? <Link to="/signup" className="text-accent underline">Create an account</Link>
            <span className="text-muted"> · </span>
            <Link to="/forgot-password" className="text-accent underline">Forgot password?</Link>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            <Input
              type="email"
              placeholder="you@example.com"
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <Alert tone="danger">{error}</Alert>}
            {needsVerification && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={resending || !email}
                onClick={onResend}
              >
                {resending ? "Re-sending…" : "Re-send verification email"}
              </Button>
            )}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isLoading || !email || !password}
            >
              {isLoading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
